import { FC, useEffect, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useDispatch, useSelector } from 'react-redux';
import {
	clearDirectDebitCreate,
	createDirectDebitRequest,
	getBanksRequest,
	getClientAccessRequest,
	getServicesRequest,
} from 'redux/reducers/paymentMethods/reducer';
import {
	getDirectDebitBanks,
	getDirectDebitBanksLoading,
	getDirectDebitCreate,
	getDirectDebitCreateLoading,
	getPaymentMethodsBlockedMessage,
	getPaymentMethodsSummary,
	getPaymentServices,
} from 'redux/reducers/paymentMethods/selectors';
import { TServicesSelection } from 'services/api/paymentMethods/types';
import { Notice } from 'ui/Notice';
import { notificationContainer } from 'utils/notificationContainer';
import { DEFAULT_ERROR_MESSAGE } from 'utils/apiError';
import { isServicesSelectionValid, ServicesSelector } from '../../ServicesSelector';
import { ClientAccessBanner } from '../../ClientAccess';

interface DirectDebitModalProps {
	open: boolean;
	onClose: () => void;
}

const DEFAULT_SELECTION: TServicesSelection = { allServices: true, serviceIds: [] };

// the browser is sent only to an https page (the bank confirmation of Airwallex / TrueLayer)
const isHttpsUrl = (url: string): boolean => {
	try {
		return new URL(url).protocol === 'https:';
	} catch {
		return false;
	}
};

export const DirectDebitModal: FC<DirectDebitModalProps> = ({ open, onClose }) => {
	const dispatch = useDispatch();
	const summary = useSelector(getPaymentMethodsSummary);
	const services = useSelector(getPaymentServices);
	const banks = useSelector(getDirectDebitBanks);
	const banksLoading = useSelector(getDirectDebitBanksLoading);
	const created = useSelector(getDirectDebitCreate);
	const creating = useSelector(getDirectDebitCreateLoading);
	const blockedMessage = useSelector(getPaymentMethodsBlockedMessage);

	const [selection, setSelection] = useState<TServicesSelection>(DEFAULT_SELECTION);
	const [selectedBankId, setSelectedBankId] = useState<string | null>(null);
	// the browser is being sent to the bank
	const [leaving, setLeaving] = useState(false);

	useEffect(() => {
		if (!open) return;
		setSelection(DEFAULT_SELECTION);
		setSelectedBankId(null);
		setLeaving(false);
		dispatch(clearDirectDebitCreate());
		dispatch(getClientAccessRequest());
		if (!banks) dispatch(getBanksRequest());
		if (!services) dispatch(getServicesRequest());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// The mandate is confirmed at the bank (TrueLayer); the bank returns the client to /payment-methods.
	useEffect(() => {
		if (!open || !created?.verificationUrl) return;
		dispatch(clearDirectDebitCreate());
		if (!isHttpsUrl(created.verificationUrl)) {
			notificationContainer(DEFAULT_ERROR_MESSAGE, 'error');
			return;
		}
		setLeaving(true);
		window.location.href = created.verificationUrl;
	}, [open, created, dispatch]);

	const ddDay = summary?.schedule?.ddDay;
	const selectionValid = isServicesSelectionValid(selection);
	const redirecting = creating || leaving || !!created;
	// blocked by the operator: read-only (a mandate already on its way to the bank is not interrupted)
	const blocked = !!blockedMessage && !redirecting;

	const handleBank = (bankId: string) => {
		if (!selectionValid || redirecting || blocked) return;
		setSelectedBankId(bankId);
		dispatch(
			createDirectDebitRequest({
				bankId,
				allServices: selection.allServices,
				serviceIds: selection.allServices ? [] : selection.serviceIds,
			}),
		);
	};

	return (
		<Dialog
			open={open}
			onClose={redirecting ? undefined : onClose}
			maxWidth="sm"
			fullWidth
			aria-labelledby="direct-debit-dialog-title"
			sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
		>
			<DialogTitle id="direct-debit-dialog-title" color="primary" sx={{ fontWeight: 'bold' }}>
				Set up Direct Debit
			</DialogTitle>
			<DialogContent>
				{blocked && <ClientAccessBanner messages={[blockedMessage]} sx={{ mb: 1 }} />}
				<Notice sx={{ mt: 0, mb: 2, width: 'auto' }}>
					{ddDay ? `Direct Debit is collected on day ${ddDay} of each month. ` : ''}
					Setting up a new Direct Debit takes up to 2 working days.
				</Notice>

				<Typography color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
					Use for services
				</Typography>
				<ServicesSelector
					services={services}
					value={selection}
					onChange={setSelection}
					disabled={redirecting || blocked}
				/>

				<Typography color="primary" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
					Choose your bank
				</Typography>
				{banksLoading && !banks && <CircularProgress size={24} />}
				{!banksLoading && banks?.length === 0 && (
					<Typography>No banks are available at the moment. Please try again later.</Typography>
				)}
				{banks?.map((bank) => {
					const disabled = !selectionValid || redirecting || blocked;
					return (
						<Box
							key={bank.id}
							role="button"
							tabIndex={disabled ? -1 : 0}
							aria-disabled={disabled}
							onClick={() => handleBank(bank.id)}
							onKeyDown={(event) => {
								if (event.key === 'Enter' || event.key === ' ') {
									event.preventDefault();
									handleBank(bank.id);
								}
							}}
							sx={{
								mt: 1,
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
								border: '1px solid #0092de24',
								borderRadius: '12px',
								padding: '10px',
								cursor: disabled ? 'default' : 'pointer',
								opacity: disabled && selectedBankId !== bank.id ? 0.5 : 1,
								transition: 'all 0.3s ease',
								'&:hover': disabled ? {} : { borderColor: 'primary.main' },
							}}
						>
							{bank.logoUrl ? (
								<img src={bank.logoUrl} alt="" style={{ width: '40px', height: '40px' }} />
							) : (
								<AccountBalanceIcon color="primary" sx={{ width: '40px', height: '40px' }} />
							)}
							<Typography sx={{ fontSize: '16px', fontWeight: 'bold', color: 'primary.main' }}>
								{bank.name}
							</Typography>
							{redirecting && selectedBankId === bank.id && (
								<CircularProgress size={20} sx={{ ml: 'auto' }} />
							)}
						</Box>
					);
				})}
				{!selectionValid && (
					<Typography sx={{ mt: 1, fontSize: '.8rem', color: 'error.main' }}>
						Select at least one service.
					</Typography>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={redirecting}>
					Cancel
				</Button>
			</DialogActions>
		</Dialog>
	);
};
